import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import EmailIcon from "assets/images/email.png";
import NotesIcon from "assets/images/notes.png";
import {
  Avatar,
  BookIcon,
  BulbIcon,
  BulbYellowIcon,
  ComputerIcon,
  DownloadCurvedIcon,
  EditIcon,
  EmailTickIcon,
  FileIcon,
  GlobeCheckmarkIcon,
  Lightbox,
  LockIcon,
  MobileDeviceIcon,
  PinFilledIcon,
  PinIcon,
  // ReplyIcon,
  TeamsAppIcon,
  Tooltip,
  TwoChatBubbleIcon,
  UserCircleIcon,
  WebPortalIcon,
} from "components";
import { useGlobalStore } from "controller";
import dayjs from "dayjs";
import { memo, useMemo, useState } from "react";
import type { ChatAttachmentResult, ChatTimelineMeta } from "shared/types";
import { ChatMessageEditorWithDictation } from "./ChatMessageEditorWithDictation";
import { SafeHtmlRenderer } from "./SafeHtmlRenderer";
import {
  buildTimelineSentence,
  type TimelineSender,
} from "./utils/buildTimelineSentence";

function hasTextContent(html: string): boolean {
  if (/<img\s/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
}

/**
 * Returns whether a message is eligible for inline editing.
 *
 * Rules:
 *  - all access specifiers (public, private, self)
 *  - normal message type only
 *  - NOT a thread root
 *  - NOT resolved
 *  - sender must be the current user
 *  - within 60 seconds of creation
 */
export function canEditMessage(
  message: {
    access_specifier: string;
    message_type?: string;
    is_thread_root?: boolean;
    mark_as_resolved?: boolean;
    sender: { mapping_id: string };
    created_at: string;
  },
  currentUserId: string,
): boolean {
  if (message.message_type !== "normal") return false;
  if (message.is_thread_root === true) return false;
  if (message.mark_as_resolved === true) return false;
  if (message.sender.mapping_id !== currentUserId) return false;
  const elapsed = Date.now() - new Date(message.created_at).getTime();
  if (elapsed > 60000) return false;
  return true;
}

// ─── Reply Excerpt (quoted parent message inside bubble) ─────────────

const ReplyExcerpt = memo(function ReplyExcerpt({
  senderDisplayName,
  excerpt,
  date,
  onClick,
}: {
  senderDisplayName: string;
  excerpt: string;
  date?: string;
  onClick?: () => void;
}) {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);

  return (
    <Box
      // onClick={onClick}
      sx={{
        p: "8px",
        mb: "8px",
        border: `1px solid ${theme.palette.neutral["010"]}`,
        borderRadius: "8px",
        backgroundColor: "#FFF",
        boxShadow: "0px 0px 4px 0px #0000000F, 0px 0px 2px 0px #0000000A",
        // cursor: onClick ? "pointer" : undefined,
      }}
    >
      <Stack
        sx={{
          borderLeft: `4px solid ${theme.palette.neutral["010"]}`,
          pl: "8px",
        }}
      >
        <Stack direction="row" alignItems="center" gap="4px">
          <Typography variant="caption-2">{senderDisplayName}</Typography>
          <svg
            width="2"
            height="2"
            viewBox="0 0 2 2"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>circle</title>
            <circle cx="1" cy="1" r="1" fill="#767B85" />
          </svg>
          {date && (
            <Typography variant="body-xsmall" color={theme.palette.lightBg.low}>
              {dayjs(date).tz(timezone).format("hh:mm A")}
            </Typography>
          )}
        </Stack>
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.lightBg.medium,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {excerpt}
        </Typography>
      </Stack>
    </Box>
  );
});

// ─── Thread Reply Indicator ("N replies") ────────────────────────────

const ThreadReplyIndicator = memo(function ThreadReplyIndicator({
  replyCount,
  onClick,
}: {
  replyCount: number;
  onClick?: () => void;
}) {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap="4px"
      role="button"
      aria-label="View thread replies"
      onClick={onClick}
      sx={{ mt: "4px", cursor: "pointer" }}
    >
      <TwoChatBubbleIcon
        sx={{ width: "16px", height: "16px" }}
        htmlColor={theme.palette.primary.main}
      />
      <Typography variant="button-small" color={theme.palette.primary.main}>
        {replyCount} {replyCount === 1 ? "reply" : "replies"}
      </Typography>
    </Stack>
  );
});

// ─── Member Timeline Divider ──────────────────────────────────────────

export const MemberTimelineBlock = memo(function MemberTimelineBlock({
  sender,
  timelineMeta,
  resourceId,
}: {
  sender: TimelineSender;
  timelineMeta?: ChatTimelineMeta;
  resourceId: string;
}) {
  const theme = useTheme();
  const segments = buildTimelineSentence(timelineMeta, sender, resourceId);

  if (segments.length === 0) return null;

  return (
    <Box sx={{ px: "8px", py: "8px", minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        sx={{ width: "100%", minWidth: 0 }}
      >
        <Box
          sx={{
            flex: 1,
            height: "1px",
            backgroundColor: "transparent",
          }}
        />
        <Typography
          variant="body-small"
          sx={{
            color: theme.palette.lightBg.low,
            whiteSpace: "normal",
            textAlign: "center",
            wordBreak: "break-word",
            minWidth: 0,
          }}
        >
          {segments.map((seg, index) =>
            seg.emphasis ? (
              <Typography
                key={`${index}-${seg.text}`}
                component={"span"}
                variant="body-small"
                color={theme.palette.primary.main}
              >
                {seg.text}
              </Typography>
            ) : (
              <span key={`${index}-${seg.text}`}>{seg.text}</span>
            ),
          )}
        </Typography>
        <Box
          sx={{
            flex: 1,
            height: "1px",
            backgroundColor: "transparent",
          }}
        />
      </Stack>
    </Box>
  );
});

// message block for resource messages
export const ResourceMessage = memo(function ResourceMessage({
  message,
  isMarkedAsResolved,
  onMarkAsResolvedClick,
  onAddToKBClick,
  onPinClick,
  onReplyClick,
  onEditClick,
  onThreadClick,
  isPinned,
  date,
  type,
  attachments,
  senderDisplayName,
  imageUrl,
  accessSpecifier,
  isEdited,
  messagePlatform,
  isFirstInGroup = true,
  isHighlighted,
  excerpt,
  excerptSenderName,
  excerptCreatedAt,
  threadReplyCount,
  isLastInGroup,
  isEditing,
  onEditSave,
  onEditCancel,
  chatId,
  sentAsEmail,
  messageSource,
}: {
  message: string;
  type?: string;
  isMarkedAsResolved: boolean;
  onMarkAsResolvedClick: () => void;
  onAddToKBClick?: () => void;
  onPinClick?: () => void;
  onReplyClick?: () => void;
  onEditClick?: () => void;
  onThreadClick?: () => void;
  isPinned?: boolean;
  date: string;
  attachments?: Array<ChatAttachmentResult>;
  senderDisplayName: string;
  imageUrl: string;
  accessSpecifier: string;
  isEdited?: boolean;
  messagePlatform?: string | null;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  isHighlighted?: boolean;
  excerpt?: string | null;
  excerptSenderName?: string | null;
  excerptCreatedAt?: string | null;
  threadReplyCount?: number;
  isEditing?: boolean;
  onEditSave?: (chatId: string, newContent: string) => Promise<void>;
  onEditCancel?: () => void;
  chatId?: string;
  sentAsEmail?: boolean | null;
  messageSource?: "canned_notes" | "email" | "time_entry_notes" | "normal";
}) {
  const theme = useTheme();

  const [isHovered, setIsHovered] = useState(false);
  const hasAttachments = !!attachments && attachments.length > 0;
  const hasMessage = hasTextContent(message);
  const isFromEmail = messageSource === "email";

  const highlightSx = useMemo(
    () =>
      isHighlighted
        ? {
            "@keyframes highlightBorder": {
              "0%": { borderColor: theme.palette.primitive.system.amber[800] },
              "100%": { borderColor: theme.palette.neutral["005"] },
            },
            borderColor: theme.palette.error.main,
            animation: "highlightBorder 1.5s ease-out forwards",
          }
        : undefined,
    [isHighlighted, theme.palette.error.main, theme.palette.neutral],
  );

  return (
    <Box
      component="div"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        mt: isFirstInGroup ? "20px" : "2px",
        alignSelf: "flex-end",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        pt: isFirstInGroup ? "20px" : "0px",
        minWidth: "200px",
        maxWidth: "70%",
        gap: "2px",
      }}
    >
      {isFirstInGroup && (
        <Stack
          justifyContent={"flex-end"}
          direction="row"
          alignItems="center"
          gap="8px"
        >
          <Typography variant="caption-2" sx={{ textTransform: "capitalize" }}>
            {senderDisplayName}
          </Typography>
          <MessageTimestamp date={date} />
        </Stack>
      )}
      {hasAttachments &&
        !isFromEmail &&
        attachments.map((att, index) => {
          const isFirstBlock = index === 0;
          const isLastBlock = index === attachments.length - 1 && !hasMessage;
          return (
            <Box
              key={att._id ?? index}
              sx={{
                position: isFirstBlock || isLastBlock ? "relative" : undefined,
                alignSelf: "flex-end",
                maxWidth: "100%",
                overflowWrap: "break-word",
                borderRadius: "8px 0px 8px 8px",
                backgroundColor: theme.palette.secondary["purple-tint-3"],
                minWidth: "200px",
                padding: "12px",
                border: `1px solid ${theme.palette.neutral["005"]}`,
                ...(hasMessage ? undefined : highlightSx),
              }}
            >
              {isFirstBlock && (
                <MarkAsResolutionIcon
                  isHovered={isHovered}
                  onMarkAsResolvedClick={onMarkAsResolvedClick}
                  onAddToKBClick={onAddToKBClick}
                  onPinClick={onPinClick}
                  onReplyClick={onReplyClick}
                  onEditClick={onEditClick}
                  isPinned={isPinned}
                  markedAsResolved={isMarkedAsResolved}
                  type="resource"
                />
              )}
              <AttachmentMessageBlock attachment={att} />
              {isLastBlock && (
                <>
                  {isMarkedAsResolved && (
                    <MarkedAsResolved
                      onClick={onMarkAsResolvedClick}
                      isHovered={isHovered}
                    />
                  )}
                  <MessageFooter
                    senderType={"resource"}
                    accessSpecifier={accessSpecifier}
                    isEdited={isEdited}
                    messagePlatform={messagePlatform}
                    sentAsEmail={sentAsEmail}
                    type={messageSource}
                  />
                </>
              )}
            </Box>
          );
        })}
      {hasMessage && (
        <Box
          sx={{
            position: "relative",
            alignSelf: "flex-end",
            maxWidth: "100%",
            overflowWrap: "break-word",
            borderRadius: "8px 0px 8px 8px",
            backgroundColor: theme.palette.primitive.system.violet[50],
            minWidth: "200px",
            padding: "12px",
            border: `1px solid ${theme.palette.neutral["005"]}`,
            ...highlightSx,
          }}
        >
          {(!hasAttachments || isFromEmail) && (
            <MarkAsResolutionIcon
              isHovered={isHovered}
              onMarkAsResolvedClick={onMarkAsResolvedClick}
              onAddToKBClick={onAddToKBClick}
              onPinClick={onPinClick}
              onReplyClick={onReplyClick}
              onEditClick={onEditClick}
              isPinned={isPinned}
              markedAsResolved={isMarkedAsResolved}
              type="resource"
            />
          )}
          {isFirstInGroup && excerpt && (
            <ReplyExcerpt
              senderDisplayName={excerptSenderName ?? senderDisplayName}
              excerpt={excerpt}
              date={excerptCreatedAt ?? undefined}
              onClick={onThreadClick}
            />
          )}
          {isEditing && chatId ? (
            <ChatMessageEditorWithDictation
              initialContent={message}
              onSave={(newContent) => onEditSave?.(chatId, newContent)}
              onCancel={() => onEditCancel?.()}
              isSaving={false}
            />
          ) : (
            <QuillModalWrapper message={message} />
          )}
          {isMarkedAsResolved && (
            <MarkedAsResolved
              onClick={onMarkAsResolvedClick}
              isHovered={isHovered}
            />
          )}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <MessageFooter
              senderType={"resource"}
              accessSpecifier={accessSpecifier}
              isEdited={isEdited}
              messagePlatform={messagePlatform}
              sentAsEmail={sentAsEmail}
              type={messageSource}
            />
            {isLastInGroup && !!threadReplyCount && threadReplyCount > 0 && (
              <ThreadReplyIndicator
                replyCount={threadReplyCount}
                onClick={onThreadClick}
              />
            )}
          </Box>
        </Box>
      )}
      {isFromEmail &&
        hasAttachments &&
        attachments.map((att, index) => {
          const isFirstBlock = index === 0 && !hasMessage;
          const isLastBlock = index === attachments.length - 1;
          return (
            <Box
              key={att._id ?? index}
              sx={{
                position: isFirstBlock || isLastBlock ? "relative" : undefined,
                alignSelf: "flex-end",
                maxWidth: "100%",
                overflowWrap: "break-word",
                borderRadius: "8px 0px 8px 8px",
                backgroundColor: theme.palette.secondary["purple-tint-3"],
                minWidth: "200px",
                padding: "12px",
                border: `1px solid ${theme.palette.neutral["005"]}`,
                ...(hasMessage ? undefined : highlightSx),
              }}
            >
              {isFirstBlock && (
                <MarkAsResolutionIcon
                  isHovered={isHovered}
                  onMarkAsResolvedClick={onMarkAsResolvedClick}
                  onAddToKBClick={onAddToKBClick}
                  onPinClick={onPinClick}
                  onReplyClick={onReplyClick}
                  onEditClick={onEditClick}
                  isPinned={isPinned}
                  markedAsResolved={isMarkedAsResolved}
                  type="resource"
                />
              )}
              <AttachmentMessageBlock attachment={att} isFromEmail />
              {isLastBlock && (
                <>
                  {isMarkedAsResolved && (
                    <MarkedAsResolved
                      onClick={onMarkAsResolvedClick}
                      isHovered={isHovered}
                    />
                  )}
                  <MessageFooter
                    senderType={"resource"}
                    accessSpecifier={accessSpecifier}
                    isEdited={isEdited}
                    messagePlatform={messagePlatform}
                    sentAsEmail={sentAsEmail}
                    type={messageSource}
                  />
                </>
              )}
            </Box>
          );
        })}
    </Box>
  );
});

// message block for customer messages
export const CustomerMessage = memo(function CustomerMessage({
  message,
  senderDisplayName,
  imageUrl,
  isMarkedAsResolved,
  onMarkAsResolvedClick,
  type,
  onAddToKBClick,
  onPinClick,
  onReplyClick,
  onEditClick,
  onThreadClick,
  isPinned,
  attachments,
  accessSpecifier,
  isEdited,
  date,
  messagePlatform,
  isFirstInGroup = true,
  isHighlighted,
  excerpt,
  excerptSenderName,
  excerptCreatedAt,
  threadReplyCount,
  isLastInGroup,
  isEditing,
  onEditSave,
  onEditCancel,
  chatId,
  sentAsEmail,
  messageSource,
  senderUserType,
  isHelenaUser = false,
}: {
  message: string;
  senderDisplayName: string;
  imageUrl: string;
  isMarkedAsResolved: boolean;
  type?: string;
  attachments?: Array<ChatAttachmentResult>;
  onAddToKBClick?: () => void;
  onPinClick?: () => void;
  onReplyClick?: () => void;
  onEditClick?: () => void;
  onThreadClick?: () => void;
  isPinned?: boolean;
  accessSpecifier: string;
  isEdited?: boolean;
  date: string;
  onMarkAsResolvedClick: () => void;
  messagePlatform: string | null;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  isHighlighted?: boolean;
  excerpt?: string | null;
  excerptSenderName?: string | null;
  excerptCreatedAt?: string | null;
  threadReplyCount?: number;
  isEditing?: boolean;
  onEditSave?: (chatId: string, newContent: string) => Promise<void>;
  onEditCancel?: () => void;
  chatId?: string;
  sentAsEmail?: boolean | null;
  messageSource?: "canned_notes" | "email" | "time_entry_notes" | "normal";
  senderUserType?: "customer" | "resource";
  isHelenaUser?: boolean;
}) {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const hasAttachments = !!attachments && attachments.length > 0;
  const hasMessage = hasTextContent(message);
  const isFromEmail = messageSource === "email";
  // messages from other resources render here too — they still carry an
  // access specifier (public / private / self)
  const isSenderResource = senderUserType === "resource";

  const highlightSx = useMemo(
    () =>
      isHighlighted
        ? {
            "@keyframes highlightBorder": {
              "0%": { borderColor: theme.palette.error.main },
              "100%": { borderColor: "transparent" },
            },
            borderColor: theme.palette.error.main,
            animation: "highlightBorder 1.5s ease-out forwards",
          }
        : undefined,
    [isHighlighted, theme.palette.error.main],
  );

  return (
    <Box>
      <Stack
        component="div"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          alignSelf: "flex-start",
          maxWidth: "70%",
          gap: "2px",
          mt: isFirstInGroup ? "20px" : "2px",
          width: "100%",
          minWidth: "420px",
        }}
      >
        {isFirstInGroup && (
          <Stack direction="row" alignItems="center" gap="8px">
            <Avatar
              variants="sm"
              fullname={senderDisplayName}
              url={imageUrl}
              isAgent={isHelenaUser}
            />
            <Typography
              variant="caption-2"
              sx={{ textTransform: "capitalize" }}
            >
              {senderDisplayName}
            </Typography>
            <MessageTimestamp date={date} />
          </Stack>
        )}
        {hasAttachments &&
          !isFromEmail &&
          attachments.map((att, index) => {
            const isFirstBlock = index === 0;
            const isLastBlock = index === attachments.length - 1 && !hasMessage;
            return (
              <Box
                key={att._id ?? index}
                sx={{
                  position:
                    isFirstBlock || isLastBlock ? "relative" : undefined,
                  alignSelf: "flex-start",
                  maxWidth: "100%",
                  overflowWrap: "break-word",
                  borderRadius: "0px 8px 8px 8px",
                  backgroundColor: theme.palette.neutral["005"],
                  minWidth: "200px",
                  padding: "12px",
                  ml: "32px",
                  border: "1px solid transparent",
                  ...(hasMessage ? undefined : highlightSx),
                }}
              >
                {isFirstBlock && (
                  <MarkAsResolutionIcon
                    isHovered={isHovered}
                    onMarkAsResolvedClick={onMarkAsResolvedClick}
                    onAddToKBClick={onAddToKBClick}
                    onPinClick={onPinClick}
                    onReplyClick={onReplyClick}
                    onEditClick={onEditClick}
                    isPinned={isPinned}
                    markedAsResolved={isMarkedAsResolved}
                    type="customer"
                  />
                )}
                <AttachmentMessageBlock attachment={att} />
                {isLastBlock && (
                  <>
                    {isMarkedAsResolved && (
                      <MarkedAsResolved
                        onClick={onMarkAsResolvedClick}
                        isHovered={isHovered}
                      />
                    )}
                    <MessageFooter
                      senderType={"customer"}
                      showAccessSpecifier={isSenderResource}
                      accessSpecifier={accessSpecifier}
                      isEdited={isEdited}
                      messagePlatform={messagePlatform}
                      sentAsEmail={sentAsEmail}
                      type={messageSource}
                    />
                  </>
                )}
              </Box>
            );
          })}
        {hasMessage && (
          <Box
            sx={{
              position: "relative",
              alignSelf: "flex-start",
              maxWidth: "100%",
              overflowWrap: "break-word",
              borderRadius: "0px 8px 8px 8px",
              backgroundColor: theme.palette.primitive.neutrals.neutral[25],
              minWidth: "200px",
              padding: "12px",
              ml: "32px",
              border: "1px solid transparent",
              ...highlightSx,
            }}
          >
            {(!hasAttachments || isFromEmail) && (
              <MarkAsResolutionIcon
                isHovered={isHovered}
                onMarkAsResolvedClick={onMarkAsResolvedClick}
                onAddToKBClick={onAddToKBClick}
                onPinClick={onPinClick}
                onReplyClick={onReplyClick}
                onEditClick={onEditClick}
                isPinned={isPinned}
                markedAsResolved={isMarkedAsResolved}
                type="customer"
              />
            )}
            {isFirstInGroup && excerpt && (
              <ReplyExcerpt
                senderDisplayName={excerptSenderName ?? senderDisplayName}
                excerpt={excerpt}
                date={excerptCreatedAt ?? undefined}
                onClick={onThreadClick}
              />
            )}
            {isEditing && chatId ? (
              <ChatMessageEditorWithDictation
                initialContent={message}
                onSave={(newContent) => onEditSave?.(chatId, newContent)}
                onCancel={() => onEditCancel?.()}
                isSaving={false}
              />
            ) : (
              <QuillModalWrapper message={message} />
            )}
            {isMarkedAsResolved && (
              <MarkedAsResolved
                onClick={onMarkAsResolvedClick}
                isHovered={isHovered}
              />
            )}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <MessageFooter
                senderType={"customer"}
                showAccessSpecifier={isSenderResource}
                accessSpecifier={accessSpecifier}
                isEdited={isEdited}
                messagePlatform={messagePlatform}
                sentAsEmail={sentAsEmail}
                type={messageSource}
              />
              {isLastInGroup && !!threadReplyCount && threadReplyCount > 0 && (
                <ThreadReplyIndicator
                  replyCount={threadReplyCount}
                  onClick={onThreadClick}
                />
              )}
            </Box>
          </Box>
        )}
        {isFromEmail &&
          hasAttachments &&
          attachments.map((att, index) => {
            const isFirstBlock = index === 0 && !hasMessage;
            const isLastBlock = index === attachments.length - 1;
            return (
              <Box
                key={att._id ?? index}
                sx={{
                  position:
                    isFirstBlock || isLastBlock ? "relative" : undefined,
                  alignSelf: "flex-start",
                  maxWidth: "100%",
                  overflowWrap: "break-word",
                  borderRadius: "0px 8px 8px 8px",
                  backgroundColor: theme.palette.neutral["005"],
                  minWidth: "200px",
                  padding: "12px",
                  ml: "32px",
                  border: "1px solid transparent",
                  ...(hasMessage ? undefined : highlightSx),
                }}
              >
                {isFirstBlock && (
                  <MarkAsResolutionIcon
                    isHovered={isHovered}
                    onMarkAsResolvedClick={onMarkAsResolvedClick}
                    onAddToKBClick={onAddToKBClick}
                    onPinClick={onPinClick}
                    onReplyClick={onReplyClick}
                    onEditClick={onEditClick}
                    isPinned={isPinned}
                    markedAsResolved={isMarkedAsResolved}
                    type="customer"
                  />
                )}
                <AttachmentMessageBlock attachment={att} isFromEmail />
                {isLastBlock && (
                  <>
                    {isMarkedAsResolved && (
                      <MarkedAsResolved
                        onClick={onMarkAsResolvedClick}
                        isHovered={isHovered}
                      />
                    )}
                    <MessageFooter
                      senderType={"customer"}
                      showAccessSpecifier={isSenderResource}
                      accessSpecifier={accessSpecifier}
                      isEdited={isEdited}
                      messagePlatform={messagePlatform}
                      sentAsEmail={sentAsEmail}
                      type={messageSource}
                    />
                  </>
                )}
              </Box>
            );
          })}
      </Stack>
    </Box>
  );
});

function getPreviewUrl(url: string): string {
  const lastSlash = url.lastIndexOf("/");
  if (lastSlash === -1) return `preview/${url}`;
  return `${url.substring(0, lastSlash + 1)}preview/${url.substring(lastSlash + 1)}`;
}

// Max rendered dimensions for inline image attachments
const IMAGE_MAX_WIDTH = 300;
const IMAGE_MAX_HEIGHT = 300;

/**
 * Compute the rendered box size for an inline image attachment,
 * preserving the intrinsic aspect ratio and fitting within the
 * IMAGE_MAX_WIDTH / IMAGE_MAX_HEIGHT bounds.
 */
function getImageBoxSize(
  width?: number,
  height?: number,
): { width: number; height: number } | null {
  if (!(width && height) || width <= 0 || height <= 0) return null;
  const ratio = Math.min(IMAGE_MAX_WIDTH / width, IMAGE_MAX_HEIGHT / height, 1);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

// single attachment renderer
export const AttachmentMessageBlock = memo(function AttachmentMessageBlock({
  attachment,
  isFromEmail = false,
}: {
  attachment: ChatAttachmentResult;
  isFromEmail?: boolean;
}) {
  const theme = useTheme();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isImage = !isFromEmail && attachment.file_type.includes("image");

  const imageBoxSize = useMemo(
    () => getImageBoxSize(attachment.width, attachment.height),
    [attachment.width, attachment.height],
  );

  return (
    <>
      <Box
        onClick={() => {
          if (isImage) {
            setLightboxOpen(true);
          } else {
            window.open(attachment.url, "_blank", "noopener,noreferrer");
          }
        }}
        sx={{ cursor: "pointer" }}
      >
        {isImage ? (
          <Box
            sx={{
              position: "relative",
              width: imageBoxSize ? `${imageBoxSize.width}px` : "100%",
              height: imageBoxSize ? `${imageBoxSize.height}px` : undefined,
              maxWidth: "100%",
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: theme.palette.neutral["005"],
            }}
          >
            {!imageLoaded && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: theme.palette.neutral["005"],
                }}
              >
                <CircularProgress size={24} />
              </Stack>
            )}
            <Box
              component="img"
              src={getPreviewUrl(attachment.url)}
              alt={attachment.file_name}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                const img = e.currentTarget;
                if (img.src !== attachment.url) {
                  img.src = attachment.url;
                } else {
                  setImageLoaded(true);
                }
              }}
              sx={{
                width: "100%",
                height: imageBoxSize ? "100%" : "auto",
                maxHeight: imageBoxSize ? undefined : `${IMAGE_MAX_HEIGHT}px`,
                objectFit: "cover",
                borderRadius: "8px",
                display: "block",
                opacity: imageLoaded ? 1 : 0,
                transition: "opacity 0.2s ease-in",
              }}
            />
          </Box>
        ) : (
          <Stack
            direction="row"
            gap={"8px"}
            sx={{
              alignItems: "center",
              height: "auto",
              justifyContent: "space-between",
              bgcolor: theme.palette.purple.tint,
              padding: "8px",
              borderRadius: "8px",
              border: 0,
              textDecoration: "none",
            }}
          >
            <Stack
              maxWidth={"80%"}
              direction={"row"}
              alignItems={"center"}
              gap="8px"
            >
              <Box sx={{ width: "28px", height: "28px" }}>
                <FileIcon type={attachment.file_type} />
              </Box>
              <Typography variant="caption-2" noWrap textTransform={"none"}>
                {attachment.file_name}
              </Typography>
            </Stack>
            <Box
              component="a"
              target="_blank"
              rel="noopener noreferrer"
              href={attachment.url}
            >
              <DownloadCurvedIcon htmlColor={theme.palette.purple.main} />
            </Box>
          </Stack>
        )}
      </Box>
      {lightboxOpen && (
        <Lightbox
          url={attachment.url}
          filename={attachment.file_name}
          open={lightboxOpen}
          onClose={() => {
            setLightboxOpen(false);
          }}
          fileType={attachment.file_type}
        />
      )}
    </>
  );
});

const QuillModalWrapper = memo(function QuillModalWrapper({
  message,
}: {
  message: string;
}) {
  return <SafeHtmlRenderer html={message} />;
});

export const MarkedAsResolved = memo(function MarkedAsResolved({
  onClick,
  isHovered = false,
}: {
  onClick: () => void;
  isHovered?: boolean;
}) {
  const theme = useTheme();
  return (
    <Tooltip title="Marked as resolution">
      <IconButton
        aria-label="Marked as resolution"
        onClick={onClick}
        sx={{
          ":hover": { backgroundColor: theme.palette.neutral.main },
          position: "absolute",
          bottom: "-34px",
          padding: "4px 8px",
          borderRadius: "20px",
          alignSelf: "flex-start",
          left: 0,
          bgcolor: theme.palette.neutral.main,
          boxShadow:
            "0px 0px 8px 0px rgba(0, 0, 0, 0.16), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
          mb: "-4px",
          width: isHovered ? "fit-content" : "40px",
          gap: "4px",
        }}
      >
        <BulbYellowIcon />
        {isHovered && (
          <Typography variant="body-medium">Marked as resolution</Typography>
        )}
      </IconButton>
    </Tooltip>
  );
});

export const MarkAsResolutionIcon = memo(function MarkAsResolutionIcon({
  onMarkAsResolvedClick,
  onAddToKBClick,
  onPinClick,
  onReplyClick,
  onEditClick,
  markedAsResolved,
  isPinned = false,
  isHovered = false,
  type = "resource",
}: {
  onMarkAsResolvedClick: () => void;
  onAddToKBClick?: () => void;
  onPinClick?: () => void;
  onReplyClick?: () => void;
  onEditClick?: () => void;
  markedAsResolved: boolean;
  isPinned?: boolean;
  isHovered: boolean;
  type: "customer" | "resource";
}) {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      sx={{
        display: isHovered ? "flex" : "none",
        position: "absolute",
        top: "-20px",
        p: "4px 8px",
        borderRadius: "100px",
        ...(type === "customer" ? { right: 0 } : { left: 0 }),
        backgroundColor: theme.palette.neutral.main,
        boxShadow:
          "0px 0px 8px 0px rgba(0, 0, 0, 0.16), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
      }}
    >
      {!markedAsResolved && (
        <Tooltip title="Mark as resolution">
          <IconButton
            aria-label="Mark as resolution"
            onClick={onMarkAsResolvedClick}
            sx={{
              padding: "4px 8px",
              borderRadius: "20px",
              width: "32px",
              height: "24px",
            }}
          >
            <BulbIcon
              sx={{ fontSize: "20px" }}
              htmlColor={theme.palette.lightBg.low}
            />
          </IconButton>
        </Tooltip>
      )}

      {onAddToKBClick && (
        <Tooltip title="Add to Knowledge Base">
          <IconButton
            aria-label="Add to Knowledge Base"
            onClick={onAddToKBClick}
            sx={{
              padding: "4px 8px",
              borderRadius: "20px",
              width: "32px",
              height: "24px",
            }}
          >
            <BookIcon
              sx={{ fontSize: "20px" }}
              htmlColor={theme.palette.lightBg.low}
            />
          </IconButton>
        </Tooltip>
      )}

      {onPinClick && (
        <Tooltip title={isPinned ? "Unpin message" : "Pin message"}>
          <IconButton
            aria-label={isPinned ? "Unpin message" : "Pin message"}
            onClick={onPinClick}
            sx={{
              padding: "4px 8px",
              borderRadius: "20px",
              width: "32px",
              height: "24px",
            }}
          >
            {isPinned ? (
              <PinFilledIcon
                sx={{ fontSize: "20px" }}
                htmlColor={theme.palette.primary.main}
              />
            ) : (
              <PinIcon
                sx={{ fontSize: "20px" }}
                htmlColor={theme.palette.lightBg.low}
              />
            )}
          </IconButton>
        </Tooltip>
      )}

      {/* {onReplyClick && (
        <Tooltip title="Reply">
          <IconButton
            aria-label="Reply"
            onClick={onReplyClick}
            sx={{
              padding: "4px 8px",
              borderRadius: "20px",
              width: "32px",
              height: "24px",
            }}
          >
            <ReplyIcon
              sx={{ fontSize: "20px" }}
              htmlColor={theme.palette.lightBg.low}
            />
          </IconButton>
        </Tooltip>
      )} */}

      {onEditClick && (
        <Tooltip title="Edit message">
          <IconButton
            aria-label="Edit message"
            onClick={onEditClick}
            sx={{
              padding: "4px 8px",
              borderRadius: "20px",
              width: "32px",
              height: "24px",
            }}
          >
            <EditIcon
              sx={{ fontSize: "20px" }}
              htmlColor={theme.palette.lightBg.low}
            />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
});

// ─── Module-level helpers for MessageFooter ─────────────────────────

function getMessageLabelOrIcon(
  type: "caption" | "icon",
  source: "canned_notes" | "email" | "time_entry_notes" | "normal",
  senderType: "resource" | "customer",
): string {
  switch (source) {
    case "email":
      return type === "caption"
        ? senderType === "resource"
          ? "Sent to email"
          : "From email"
        : EmailIcon;
    case "time_entry_notes":
      return type === "caption" ? "Time entry note" : NotesIcon;
    case "canned_notes":
      return type === "caption" ? "Canned note" : NotesIcon;
    default:
      return type === "caption"
        ? senderType === "resource"
          ? "Sent to Email"
          : "From email"
        : EmailIcon;
  }
}

function isImageShown(
  msgType: "email" | "time_entry_notes" | "canned_notes",
): boolean {
  return msgType !== "email";
}

const MessageTimestamp = memo(function MessageTimestamp({
  date,
}: {
  date: string;
}) {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);

  return (
    <Typography variant="body-small" color={theme.palette.lightBg.low}>
      {dayjs(date).tz(timezone).format("hh:mm A")}
    </Typography>
  );
});

export const MessageFooter = memo(function MessageFooter({
  senderType,
  accessSpecifier,
  isEdited = false,
  messagePlatform,
  sentAsEmail,
  type,
  showAccessSpecifier,
}: {
  senderType: "resource" | "customer";
  accessSpecifier: string;
  isEdited?: boolean;
  messagePlatform?: string | null;
  sentAsEmail?: boolean | null;
  type?: "canned_notes" | "email" | "time_entry_notes" | "normal";
  /** Overrides the default (senderType === "resource") visibility of the
   * public/private/self chip — messages from other resources render in the
   * customer block but still belong to a resource. */
  showAccessSpecifier?: boolean;
}) {
  const theme = useTheme();
  type = messagePlatform === "email" || sentAsEmail ? "email" : type;

  const getAccessSpecifier = (
    accessSpecifier: "private" | "self" | "public",
  ) => {
    const iconSx = { width: "16px", height: "16px" };
    switch (accessSpecifier) {
      case "public":
        return {
          label: "Public",
          color: theme.palette.primitive.system.violet[500],
          icon: (
            <GlobeCheckmarkIcon
              sx={iconSx}
              fontSize="small"
              htmlColor={theme.palette.primitive.system.violet[500]}
            />
          ),
        };
      case "self":
        return {
          label: "Self",
          color: theme.palette.lightBg.low,
          icon: (
            <UserCircleIcon
              sx={iconSx}
              fontSize="small"
              htmlColor={theme.palette.lightBg.low}
            />
          ),
        };
      case "private":
      default:
        return {
          label: "Private",
          color: theme.palette.primitive.system.blue[500],
          icon: (
            <LockIcon
              sx={iconSx}
              fontSize="small"
              htmlColor={theme.palette.primitive.system.blue[500]}
            />
          ),
        };
    }
  };
  return (
    <Stack
      mt={"8px"}
      direction="row"
      alignItems="center"
      justifyContent={"space-between"}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="12px"
        justifyContent={"flex-start"}
      >
        <PlatformIcon
          platform={
            type === "email" && senderType === "resource"
              ? type
              : messagePlatform
          }
          senderType={senderType}
        />
        {type && type !== "normal" && isImageShown(type) && (
          <Stack direction="row" alignItems="center" gap="12px">
            <Tooltip
              title={getMessageLabelOrIcon("caption", type, senderType)}
              placement="top"
              followCursor={true}
              PopperProps={{
                sx: {
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: theme.palette.neutral["B-015"],
                    color: theme.palette.lightBg.high,

                    ...theme.typography["caption"],
                    textTransform: "capitalize",
                  },
                },
              }}
            >
              {/* if email is sent as email
              and is public
               */}

              <img
                alt=""
                src={getMessageLabelOrIcon("icon", type, senderType)}
                width={"20px"}
                height={"20px"}
              />
            </Tooltip>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" gap="12px">
          {(showAccessSpecifier ?? senderType === "resource") &&
            (() => {
              const { icon, label, color } = getAccessSpecifier(
                accessSpecifier as "private" | "public" | "self",
              );
              return (
                <Stack direction="row" alignItems="center" gap="4px">
                  <Stack sx={{ width: "16px", height: "16px" }}>{icon}</Stack>
                  <Typography variant="caption-3" color={color}>
                    {label}
                  </Typography>
                </Stack>
              );
            })()}

          {isEdited && (
            <>
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  bgcolor: theme.palette.lightBg.low,
                  display: "inline-block",
                }}
              />
              <Typography
                variant="body-small"
                sx={{ textTransform: "capitalize" }}
                color={theme.palette.lightBg.low}
              >
                Edited
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

const PlatformIcon = memo(function PlatformIcon({
  platform,
  senderType,
}: {
  platform?: string | null;
  senderType?: string;
}) {
  const theme = useTheme();

  switch (platform) {
    case "agent_app":
      return (
        <Tooltip title="From Desktop">
          <Box sx={{ height: "20px" }}>
            <ComputerIcon
              fontSize="small"
              htmlColor={theme.palette.lightBg.low}
            />
          </Box>
        </Tooltip>
      );
    case "mobile_app":
      return (
        <Tooltip title="From Mobile">
          <Box sx={{ height: "20px" }}>
            <MobileDeviceIcon
              fontSize="small"
              htmlColor={theme.palette.lightBg.low}
            />
          </Box>
        </Tooltip>
      );
    case "teams_app":
      return (
        <Tooltip title="From Teams">
          <Box sx={{ height: "20px" }}>
            <TeamsAppIcon
              sx={{ width: "16px", height: "16px" }}
              fontSize="small"
              htmlColor={theme.palette.lightBg.low}
            />
          </Box>
        </Tooltip>
      );
    case "email":
      return (
        <Tooltip
          title={senderType === "resource" ? "Sent to Email" : "From Email"}
        >
          <Box sx={{ height: "20px" }}>
            <EmailTickIcon
              fontSize="small"
              htmlColor={theme.palette.lightBg.low}
            />
          </Box>
        </Tooltip>
      );
    case "agent_web":
      return (
        <Tooltip title="From Web Portal">
          <Box sx={{ height: "20px" }}>
            <WebPortalIcon
              fontSize="small"
              htmlColor={theme.palette.lightBg.low}
            />
          </Box>
        </Tooltip>
      );

    default:
      return null;
  }
});
